import React, { useMemo, useState } from 'react';
import { Button, Stack, Typography } from '@mui/material';
import { getCareSourceProfileSuggestions } from './careSourceStatusModel';
import { saveDirectProfileReviewProgress } from './careSourceActivityService';
import { applyProfileSuggestionsToChild } from './profileSuggestionApplyService';
import CollapsibleReviewGroup from './CollapsibleReviewGroup';
import CompactReviewRow from './CompactReviewRow';

const PROFILE_FIELD_DESTINATIONS = Object.freeze({
  about: 'Person',
  earlyWarningSigns: 'Watch For',
  thingsThatUsuallyHelp: 'What Helps',
  thingsToAvoid: 'Watch For',
  safetyConsiderations: 'Safety',
  communicationStyle: 'Communication',
  communicationNotes: 'Communication',
  familySupportTips: 'What Helps',
  schoolTherapySuggestions: 'Health & Care',
});

const getItems = (source = {}) => getCareSourceProfileSuggestions(source).flatMap((group) => (
  group.items.map((text, index) => ({
    id: `legacy-profile:${group.key}:${index}`,
    field: group.key,
    destination: PROFILE_FIELD_DESTINATIONS[group.key] || 'Profile',
    text,
  }))
));

const getInitialDecisions = (source = {}) => (
  source.directProfileReviewDecisions && typeof source.directProfileReviewDecisions === 'object'
    ? source.directProfileReviewDecisions
    : {}
);

const LegacyProfileSuggestionReview = ({ source = {}, onComplete }) => {
  const items = useMemo(() => getItems(source), [source]);
  const [decisions, setDecisions] = useState(() => getInitialDecisions(source));
  const [savingId, setSavingId] = useState('');
  const [error, setError] = useState('');
  const reviewedCount = items.filter((item) => decisions[item.id]).length;
  const complete = items.length > 0 && reviewedCount === items.length;
  const groups = useMemo(() => {
    const grouped = new Map();
    items.forEach((item) => {
      if (!grouped.has(item.destination)) grouped.set(item.destination, []);
      grouped.get(item.destination).push(item);
    });
    return [...grouped.entries()];
  }, [items]);

  const decide = async (item, decision) => {
    if (decisions[item.id] || savingId) return;
    setSavingId(item.id);
    setError('');
    try {
      const nextDecisions = { ...decisions, [item.id]: decision };
      await saveDirectProfileReviewProgress(source.id, {
        decisions: nextDecisions,
        savedItems: source.careKnowledgeSavedItems || [],
      });
      setDecisions(nextDecisions);
    } catch (saveError) {
      setError(saveError?.message || "We couldn't save that decision right now.");
    } finally {
      setSavingId('');
    }
  };

  const finish = async () => {
    setSavingId('complete');
    setError('');
    try {
      const acceptedUpdates = items.reduce((updates, item) => {
        if (decisions[item.id] !== 'add') return updates;
        return {
          ...updates,
          [item.field]: [...(updates[item.field] || []), item.text],
        };
      }, {});
      const result = await applyProfileSuggestionsToChild({
        childId: source.childId,
        sourceId: source.id,
        source,
        suggestedProfileUpdates: acceptedUpdates,
        allowEmpty: true,
      });
      onComplete?.({
        addedCount: result.savedItems.length,
        chapterCounts: items.reduce((counts, item) => (
          decisions[item.id] === 'add'
            ? { ...counts, [item.destination]: (counts[item.destination] || 0) + 1 }
            : counts
        ), {}),
      });
    } catch (saveError) {
      setError(saveError?.message || "We couldn't finish this review right now.");
    } finally {
      setSavingId('');
    }
  };

  if (!items.length) return null;

  return (
    <Stack spacing={1}>
      {groups.map(([destination, groupItems]) => (
        <CollapsibleReviewGroup
          key={destination}
          label={destination}
          count={groupItems.length}
          reviewedCount={groupItems.filter((item) => decisions[item.id]).length}
        >
          {groupItems.map((item) => (
            <CompactReviewRow
                  source={source}
              key={item.id}
              text={item.text}
              destination={item.destination}
              originalText={item.text}
              decision={decisions[item.id]}
              disabled={Boolean(savingId)}
              onAdd={() => decide(item, 'add')}
              onSkip={() => decide(item, 'skip')}
            />
          ))}
        </CollapsibleReviewGroup>
      ))}
      {complete ? (
        <Button
          variant="contained"
          disabled={Boolean(savingId)}
          onClick={finish}
          sx={{ textTransform: 'none', fontWeight: 500 }}
        >
          Done
        </Button>
      ) : null}
      {error ? <Typography color="error">{error}</Typography> : null}
    </Stack>
  );
};

export default LegacyProfileSuggestionReview;
